import { useMemo } from 'react';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import {
  Documentation,
  FilterGroupingEnum,
  useEdmDocumentationsAnalyticsByDocumentQuery,
} from 'graphql-common';
import { useEdmDocumentationsQuery } from '@lib/graphql-common';
import {
  GetUrlParams,
  OrderingParams,
  PaginationParams,
} from '@lib/enums/urls';
import getPeriodInput from '../utils/getPeriodInput';
import getAnalyticsDocumentSorts from './utils/getAnalyticsDocumentSorts';
import buildSearchFilters from './utils/buildSearchFilters';
import { AnalyticsDocumentRow } from './utils/getTopDocumentsTableColumns';

interface RawAnalyticsRow {
  documentationId: string;
  viewCount: number;
  uniqueViews: number;
  avgTime: number | null;
  minTime: number | null;
  maxTime: number | null;
}

interface UseTopDocumentsDataArgs {
  period: string[];
  searchQuery: string;
  paginationParams: PaginationParams;
  orderingParams: OrderingParams;
  perPageDefault: number;
}

export interface TopDocumentsDataResult {
  rows: AnalyticsDocumentRow[];
  totalCount: number;
  loading: boolean;
  refetchAnalytics: () => void;
}

/**
 * Orchestrates the two queries that power the Top Documents table:
 *  1. analytics-by-document (paginated aggregates with view counts)
 *  2. documentations (full Documentation objects for the rows on the page)
 *
 * Combines both into `AnalyticsDocumentRow[]` ready for the table.
 */
export default function useTopDocumentsData({
  period,
  searchQuery,
  paginationParams,
  orderingParams,
  perPageDefault,
}: UseTopDocumentsDataArgs): TopDocumentsDataResult {
  const periodInput = getPeriodInput(period);
  const page = Number(paginationParams[GetUrlParams.Page] || '1');
  const limit = Number(
    paginationParams[GetUrlParams.PerPage] || String(perPageDefault),
  );
  const sorts = getAnalyticsDocumentSorts(
    orderingParams[GetUrlParams.OrderingField],
    orderingParams[GetUrlParams.OrderingDirection],
  );
  const filters = buildSearchFilters(searchQuery);

  const analyticsQuery = useEdmDocumentationsAnalyticsByDocumentQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      period: periodInput || { startDate: '', endDate: '' },
      page,
      limit,
      sorts,
      filters: filters.length ? filters : undefined,
    },
    skip: !periodInput,
  });

  const analyticsRows = _get(
    analyticsQuery,
    ['data', 'data', 'collection'],
    [],
  ) as RawAnalyticsRow[];

  const totalCount = _get(
    analyticsQuery,
    ['data', 'data', 'metadata', 'totalCount'],
    0,
  );

  const documentationIds = analyticsRows.map((r) => r.documentationId);

  const documentationsQuery = useEdmDocumentationsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      page: 1,
      limit: documentationIds.length || 1,
      filters: documentationIds.length
        ? [
            {
              id: {
                grouping: FilterGroupingEnum.And,
                predicate: { in: documentationIds },
              },
            },
          ]
        : undefined,
    },
    skip: !documentationIds.length,
  });

  const documentationsById = useMemo(() => {
    const collection = _get(
      documentationsQuery,
      ['data', 'data', 'collection'],
      [],
    ) as Documentation[];
    return _keyBy(collection, 'id');
  }, [documentationsQuery]);

  const rows: AnalyticsDocumentRow[] = useMemo(
    () =>
      analyticsRows.map((row) => ({
        id: row.documentationId,
        documentationId: row.documentationId,
        documentation: documentationsById[row.documentationId],
        viewCount: row.viewCount,
        uniqueViews: row.uniqueViews,
        avgTime: row.avgTime ?? null,
        minTime: row.minTime ?? null,
        maxTime: row.maxTime ?? null,
      })),
    [analyticsRows, documentationsById],
  );

  return {
    rows,
    totalCount,
    // Include the second query so the table stays in its loading state until
    // the full Documentation objects (name, icon, links) are joined onto the rows.
    loading: analyticsQuery.loading || documentationsQuery.loading,
    refetchAnalytics: () => {
      analyticsQuery.refetch();
    },
  };
}
